import { Button, Textarea } from '@mantine/core';
import { type ChangeEvent, useState } from 'react';

import { MAX_COMMUNICATION_LENGTH } from '@/constants/communication.ts';

import { useCommunicationSubmit } from '@/hooks/use-communication-submit.ts';

import { ModalType } from '@/models/Modal.ts';
import { CommunicationContext } from '@/models/communication-context.ts';
import { PendingAction } from '@/models/communication-pending-action.ts';

import { closeModal } from '@/services/modalService.tsx';

interface Props {
    context: CommunicationContext;
    onSavedWithTask: (content: string) => void;
}

const handleCancel = (): void => closeModal(ModalType.COMMUNICATION);

export const CommunicationModal = ({ context, onSavedWithTask }: Props) => {
    const [content, setContent] = useState('');
    const { submit, pendingAction, isSubmitting } = useCommunicationSubmit(context);

    const trimmedContent = content.trim();
    const isEmpty = trimmedContent.length === 0;

    const handleContentChange = (event: ChangeEvent<HTMLTextAreaElement>): void =>
        setContent(event.currentTarget.value);

    const save = async (action: PendingAction): Promise<void> => {
        const isSaved = await submit(action, trimmedContent);
        if (!isSaved) return;

        closeModal(ModalType.COMMUNICATION);
        if (action === PendingAction.SAVE_WITH_TASK) onSavedWithTask(trimmedContent);
    };

    return (
        <div className="flex flex-col gap-4">
            <Textarea
                label="Kommunikation"
                placeholder="Kommunikation erfassen…"
                description={`Wird in Insuite in der Akte von ${context.patientName} abgelegt.`}
                value={content}
                onChange={handleContentChange}
                rows={8}
                maxLength={MAX_COMMUNICATION_LENGTH}
                disabled={isSubmitting}
                data-autofocus
            />
            <p className="-mt-2 text-right text-xs text-slate-500 dark:text-neutral-400">
                {content.length}/{MAX_COMMUNICATION_LENGTH}
            </p>
            <div className="flex justify-end gap-2">
                <Button variant="default" onClick={handleCancel} disabled={isSubmitting}>
                    Abbrechen
                </Button>
                <Button
                    variant="outline"
                    onClick={() => save(PendingAction.SAVE_WITH_TASK)}
                    disabled={isEmpty || pendingAction === PendingAction.SAVE}
                    loading={pendingAction === PendingAction.SAVE_WITH_TASK}
                >
                    Speichern + Aufgabe erstellen
                </Button>
                <Button
                    onClick={() => save(PendingAction.SAVE)}
                    disabled={isEmpty || pendingAction === PendingAction.SAVE_WITH_TASK}
                    loading={pendingAction === PendingAction.SAVE}
                >
                    Speichern
                </Button>
            </div>
        </div>
    );
};
